import { FC, ReactNode } from "react";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";

export const CHART_COLORS = {
	primary: "#1176E8",
	success: "#2E7D32",
	error: "#D32F2F",
	warning: "#ED6C02",
	neutral: "#9E9E9E",
	secondary: "#7B1FA2",
};

export const ChartCard: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 320 }}>
		<Typography fontWeight={600} fontSize={15} sx={{ mb: 2 }}>
			{title}
		</Typography>
		{children}
	</Paper>
);


const EmptyChart = () => (
	<Typography color="text.secondary" fontSize={13}>
		No data for the selected filters.
	</Typography>
);

export type StackedBarSegment = { label: string; value: number; color: string };
export type StackedBarRow = { label: string; segments: StackedBarSegment[] };

/** Horizontal stacked bars, one row per category, scaled to the largest row total. */
export const StackedBarChart: FC<{ rows: StackedBarRow[] }> = ({ rows }) => {
	const totals = rows.map((row) => row.segments.reduce((sum, s) => sum + s.value, 0));
	const max = Math.max(...totals, 0);
	if (max === 0) return <EmptyChart />;
	const legend = rows[0]?.segments ?? [];
	return (
		<Box>
			<Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
				{legend.map((s) => (
					<Stack key={s.label} direction="row" alignItems="center" gap={0.5}>
						<Box sx={{ width: 12, height: 12, borderRadius: "2px", background: s.color }} />
						<Typography fontSize={12}>{s.label}</Typography>
					</Stack>
				))}
			</Stack>
			{rows.map((row, i) => (
				<Stack key={row.label} direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
					<Typography fontSize={13} sx={{ width: 110, flex: "none" }}>
						{row.label}
					</Typography>
					<Stack direction="row" sx={{ flex: 1, height: 22, background: "#F2F4F7", borderRadius: "3px", overflow: "hidden" }}>
						{row.segments
							.filter((s) => s.value > 0)
							.map((s) => (
								<Tooltip key={s.label} title={`${s.label}: ${s.value}`} arrow>
									<Box sx={{ width: `${(s.value / max) * 100}%`, background: s.color }} />
								</Tooltip>
							))}
					</Stack>
					<Typography fontSize={12} color="text.secondary" sx={{ width: 40, textAlign: "right" }}>
						{totals[i]}
					</Typography>
				</Stack>
			))}
		</Box>
	);
};